/* Powers the Main Collection Product Grid Plus Ultra section:
   - grid-density switcher (1-4 columns, persisted per section in localStorage)
   - infinite scroll
   - view more (click to load next page)
   - expanded filter drawer (no submenu "pages"; accordion in-panel)

   Dawn's facets.js replaces #ProductGridContainer innerHTML on filter/sort
   changes. A MutationObserver re-applies density + pagination hooks after. */
if (!customElements.get('hdt-collection-grid-plus-ultra')) {
  customElements.define(
    'hdt-collection-grid-plus-ultra',
    class HdtCollectionGridPlusUltra extends HTMLElement {
      connectedCallback() {
        this.paginationType = this.dataset.paginationType;
        this.container = this.querySelector('#ProductGridContainer');
        this.densitySwitcher = this.querySelector('[data-hdt-density-switcher]');
        this.loading = false;

        this.onDensityClick = this.onDensityClick.bind(this);
        this.onViewMoreClick = this.onViewMoreClick.bind(this);
        this.onExpandedFilterClick = this.onExpandedFilterClick.bind(this);
        this.onExpandedDrawerToggle = this.onExpandedDrawerToggle.bind(this);
        this.onListFilterResize = this.onListFilterResize.bind(this);

        if (this.densitySwitcher) {
          this.densitySwitcher.addEventListener('click', this.onDensityClick);
          this.applyStoredDensity();
        }

        this.bindPagination();
        this.bindExpandedFilterDrawer();
        window.addEventListener('resize', this.onListFilterResize);

        if (this.container) {
          this.containerObserver = new MutationObserver(() => {
            this.applyStoredDensity();
            this.bindPagination();
          });
          this.containerObserver.observe(this.container, { childList: true });
        }
      }

      disconnectedCallback() {
        this.containerObserver?.disconnect();
        this.facetsObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.removeEventListener('click', this.onViewMoreClick);
        this.removeEventListener('click', this.onExpandedFilterClick, true);
        this.expandedDisclosure?.removeEventListener('toggle', this.onExpandedDrawerToggle);
        window.removeEventListener('resize', this.onListFilterResize);
      }

      bindExpandedFilterDrawer() {
        const expanded = this.querySelector('.facets-container-drawer--expanded');
        if (!expanded) return;

        this.addEventListener('click', this.onExpandedFilterClick, true);

        this.expandedDisclosure = expanded.querySelector('details.mobile-facets__disclosure');
        this.expandedDisclosure?.addEventListener('toggle', this.onExpandedDrawerToggle);
        if (this.expandedDisclosure?.open) this.expandFirstFilterGroup();

        /* Facets.js swaps filter markup after apply; remeasure list heights. */
        this.facetsObserver?.disconnect();
        const form = expanded.querySelector('#FacetFiltersFormMobile') || expanded.querySelector('.mobile-facets__main');
        if (form) {
          this.facetsObserver = new MutationObserver(() => {
            if (!this.expandedDisclosure?.open) return;
            clearTimeout(this._listHeightTimer);
            this._listHeightTimer = setTimeout(() => this.syncListFilterHeights(), 50);
          });
          this.facetsObserver.observe(form, { childList: true, subtree: true });
        }
      }

      onExpandedFilterClick(event) {
        if (!this.querySelector('.facets-container-drawer--expanded')) return;
        const summary = event.target.closest('details.mobile-facets__details > summary');
        if (!summary || !this.contains(summary)) return;

        /* Stop MenuDrawer from treating filter groups as sliding submenus,
           and toggle the accordion ourselves. */
        event.preventDefault();
        event.stopImmediatePropagation();

        const details = summary.parentElement;
        if (!details) return;
        if (details.hasAttribute('open')) {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        } else {
          details.setAttribute('open', '');
        }
        /* Recalc flex list heights after open/close. */
        requestAnimationFrame(() => this.syncListFilterHeights());
      }

      onExpandedDrawerToggle() {
        if (!this.expandedDisclosure?.open) return;
        requestAnimationFrame(() => {
          this.expandFirstFilterGroup();
          this.syncListFilterHeights();
        });
      }

      expandFirstFilterGroup() {
        const expanded = this.querySelector('.facets-container-drawer--expanded');
        if (!expanded) return;

        const groups = expanded.querySelectorAll('details.mobile-facets__details');
        groups.forEach((details, index) => {
          details.classList.remove('menu-opening');
          if (index === 0) {
            details.setAttribute('open', '');
          } else {
            details.removeAttribute('open');
          }
        });
        expanded.querySelector('.mobile-facets__main')?.classList.remove('submenu-open');
        this.syncListFilterHeights();
      }

      /* List filters share the drawer body: max-height = remaining space /
         number of open list groups (CSS flex does the split; this sets a
         pixel cap so lists never exceed the drawer). */
      syncListFilterHeights() {
        const expanded = this.querySelector('.facets-container-drawer--expanded');
        const main = expanded?.querySelector('.mobile-facets__main');
        if (!main) return;

        const openListDetails = Array.from(
          main.querySelectorAll('details.mobile-facets__details[open]:has(.mobile-facets__list)'),
        );
        const lists = openListDetails.map((details) => details.querySelector('.mobile-facets__list')).filter(Boolean);

        lists.forEach((list) => {
          list.style.maxHeight = '';
        });

        if (!lists.length) return;

        const mainHeight = main.clientHeight;
        let reserved = 0;
        main.querySelectorAll('details.mobile-facets__details').forEach((details) => {
          const summary = details.querySelector(':scope > summary');
          if (summary) reserved += summary.getBoundingClientRect().height;
          if (details.open && !details.querySelector('.mobile-facets__list')) {
            const submenu = details.querySelector(':scope > .mobile-facets__submenu');
            if (submenu) reserved += submenu.getBoundingClientRect().height;
          }
        });

        const available = Math.max(0, mainHeight - reserved);
        const perList = Math.floor(available / lists.length);

        lists.forEach((list) => {
          list.style.maxHeight = perList > 0 ? `${perList}px` : '0px';
        });
      }

      onListFilterResize() {
        if (!this.querySelector('.facets-container-drawer--expanded details.mobile-facets__disclosure[open]')) return;
        this.syncListFilterHeights();
      }

      get grid() {
        return this.querySelector('#product-grid');
      }

      bindPagination() {
        this.intersectionObserver?.disconnect();
        this.observedSentinel = null;

        if (this.paginationType === 'infinite_scroll') {
          this.observeSentinel();
        } else if (this.paginationType === 'view_more') {
          this.addEventListener('click', this.onViewMoreClick);
        }
      }

      onDensityClick(event) {
        const button = event.target.closest('.hdt-cpu-density-switcher__btn');
        if (!button) return;
        const cols = button.getAttribute('data-cols');
        this.setDensity(cols);
        try {
          localStorage.setItem(this.densitySwitcher.dataset.storageKey, cols);
        } catch (error) {
          /* storage unavailable */
        }
      }

      setDensity(cols) {
        if (!cols) return;
        const grid = this.grid;
        if (grid) {
          /* Single override used at every breakpoint so mobile (1–2) and
             desktop (1–6) density picks both take effect immediately. */
          grid.style.setProperty('--hdt-cpu-cols', cols);
          grid.style.setProperty('--hdt-cpu-cols-mobile', cols);
          grid.style.setProperty('--hdt-cpu-cols-tablet', cols);
          grid.style.setProperty('--hdt-cpu-cols-desktop', cols);
        }
        this.densitySwitcher?.querySelectorAll('.hdt-cpu-density-switcher__btn').forEach((btn) => {
          const isActive = btn.getAttribute('data-cols') === String(cols);
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', String(isActive));
        });
      }

      applyStoredDensity() {
        if (!this.densitySwitcher) return;
        let stored = null;
        try {
          stored = localStorage.getItem(this.densitySwitcher.dataset.storageKey);
        } catch (error) {
          stored = null;
        }
        if (stored) this.setDensity(stored);
      }

      observeSentinel() {
        const sentinel = this.querySelector('[id^="ProductGridSentinel-"]');
        if (!sentinel || sentinel === this.observedSentinel) return;

        this.intersectionObserver?.disconnect();
        this.observedSentinel = sentinel;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadNextPage(sentinel);
            });
          },
          { rootMargin: '400px 0px' }
        );
        this.intersectionObserver.observe(sentinel);
      }

      onViewMoreClick(event) {
        const button = event.target.closest('[data-hdt-cpu-view-more]');
        if (!button || !this.contains(button)) return;
        this.loadNextPage(button);
      }

      loadNextPage(trigger) {
        if (this.loading) return;
        const nextUrl = trigger.getAttribute('data-next-url');
        if (!nextUrl) {
          this.removeTrigger(trigger);
          return;
        }

        this.loading = true;
        trigger.classList.add('is-loading', 'loading');

        fetch(nextUrl)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const newGrid = html.querySelector('#product-grid');
            const newTrigger =
              html.querySelector('[id^="ProductGridSentinel-"]') ||
              html.querySelector('[data-hdt-cpu-view-more]');
            const grid = this.grid;

            if (newGrid && grid) {
              Array.from(newGrid.children).forEach((item) => grid.appendChild(item));
            }

            if (newTrigger && newTrigger.getAttribute('data-next-url')) {
              trigger.setAttribute('data-next-url', newTrigger.getAttribute('data-next-url'));
            } else {
              this.removeTrigger(trigger);
            }
          })
          .catch(() => {
            this.removeTrigger(trigger);
          })
          .finally(() => {
            this.loading = false;
            trigger.classList.remove('is-loading', 'loading');
          });
      }

      removeTrigger(trigger) {
        if (trigger.id && trigger.id.startsWith('ProductGridSentinel-')) {
          this.intersectionObserver?.disconnect();
          this.observedSentinel = null;
        }
        const wrap = trigger.closest('.hdt-cpu-view-more-wrap');
        (wrap || trigger).remove();
      }
    }
  );
}
